
import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";

// ** MUI Components
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Popover,
  FormControlLabel,
  Checkbox,
  MenuItem,
  InputAdornment,
  Grid,
  FormLabel,
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { CircularProgress, } from "@mui/material";

// ** Custom Components
import Icon from "components/icon";
import CustomTextField from "components/mui/text-field";
import CustomAutocomplete from "components/mui/autocomplete";
import UnitDialog from "screens/Inventory/component/UnitofMeasurement";


const AddLineItem = ({
  open,
  handleClose,
  LineItemContent,
  // ** Asset
  asset,
  assetsList,
  assetTypesList,
  handleAssetChange,
  assetErrors,
  handleSave,
  // ** Observation
  observationErrors,
  observation,
  handleObservationChange,
  // ** Material
  materialErrors,
  material,
  handleMaterialChange,
  materialTypesList,
  materialsList,
  // ** Labor
  laborErrors,
  labor,
  handleLaborChange,
  skillTypesList,
  employeeList,

  // ** Subcontractor
  subcontractorErrors,
  subcontractor,
  handleSubcontractorChange,
  subcontractorList,

  // ** Miscellaneous

  miscCostErrors,
  miscCost,
  handleMiscCostChange,
  entity,
    units = [],
  fetchUnitsData,
  handleUnitCreated
}) => {

  const DefaultSelect = { employeeID: 0, label: "Select Provided by" };
  const isActuals = entity === "jobDetailActual";
    const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [unitCallback, setUnitCallback] = useState(null);
 const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined) return '';

  const str = value.toString();

  // Allow partial numbers while typing (e.g. ".", "0.", ".5")
  if (str === '' || str === '.' || str === '0.' || /^\.\d*$/.test(str)) {
    return str;
  }

  // Remove commas and format integer part
  const parts = str.replace(/,/g, '').split('.');
  if (parts[0] !== '') {
    const num = Number(parts[0]);
    // Check if it's actually a valid number before formatting
    if (!isNaN(num)) {
      parts[0] = num.toLocaleString('en-US');
    }
  }

  return parts.join('.');
};

 const handleOpenUnitDialog = (callback) => {
    setUnitCallback(() => callback);
    setUnitDialogOpen(true);
  };

  // Handle unit creation callback
  const handleUnitCreatedCallback = (result) => {
    if (unitCallback) {
      unitCallback(result);
    }
    setUnitDialogOpen(false);
    setUnitCallback(null);
  };
 // Handle unit selection with "Add New" option for Material
  const handleMaterialUnitChange = (event, newValue) => {
    if (newValue?.value === '__ADD_NEW_UNIT__') {
      // Open unit dialog with callback to update material unit
      handleOpenUnitDialog((result) => {
        if (result && result.unitID) {
          handleMaterialChange("unitID", result.unitID);
          // Refresh units list
          if (fetchUnitsData) {
            fetchUnitsData();
          }
        }
      });
    } else {
      // Set the selected unit
      handleMaterialChange("unitID", newValue?.value || "");
    }
  };

  const handleSubcontractorUnitChange = (event, newValue) => {
    if (newValue?.value === '__ADD_NEW_UNIT__') {
      // Open unit dialog with callback to update material unit
      handleOpenUnitDialog((result) => {
        if (result && result.unitID) {
          handleSubcontractorChange("unitID", result.unitID);
          // Refresh units list
          if (fetchUnitsData) {
            fetchUnitsData();
          }
        }
      });
    } else {
      // Set the selected unit
      handleSubcontractorChange("unitID", newValue?.value || "");
    }
  };

  // Prepare unit options including "Add New" option
  const unitOptions = [
    { value: '__ADD_NEW_UNIT__', label: '+ Create New Unit', isAddNew: true },
    ...(units || []).map(unit => ({
      value: unit.value,
      label: unit.label
    }))
  ];


  const removeCommas = (value) => value.replace(/,/g, '');

  const Asset = useMemo(() => (
    <>
      <Grid container spacing={2}>
       <Grid item xs={12} md={6}>
            <CustomAutocomplete
              fullWidth
              clearOnBlur={false}  
              clearOnEscape
              required
              options={[
                { typeCatalogID: 0, typeName: "Select Asset Type"}, 
                ...((assetTypesList || [])
                    .sort((a, b) => (a.typeName || '').localeCompare(b.typeName || ''))
                )
              ]}
              getOptionLabel={(option) => {
                if (!option) return '';
                if (option.typeCatalogID === 0) return "Select Asset Type";
                return `${option?.typeName || ''}`.trim();
              }}
              value={
                asset?.assetTypeID
                  ? assetTypesList.find(item => Number(item.typeCatalogID) === Number(asset.assetTypeID))
                  : { typeCatalogID: 0, typeName: "Select Asset Type"}
              }          
              onChange={(event, newValue) => {              
                  handleAssetChange("assetTypeID", newValue.typeCatalogID || 0);              
              }}
              isOptionEqualToValue={(option, value) =>
                option?.typeCatalogID === value?.typeCatalogID
              }
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option => 
                  option.typeCatalogID === 0 || // Always include the "Select Asset Type" option
                  (option.typeName || '').toLowerCase().includes(searchTerm)
                );
              }}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Asset Type"
                  placeholder="Select Asset Type"
                  required
                  error={!!assetErrors?.assetTypeID}
                  helperText={assetErrors?.assetTypeID}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>          
            <CustomAutocomplete
              fullWidth
              required
              clearOnBlur={false}  
              clearOnEscape
              disabled={!asset?.assetTypeID}
              options={[
                { assetID: 0, assetName: "Select Asset"}, 
                ...((assetsList.filter(x => x.assetTypeID === asset?.assetTypeID) || [])
                    .sort((a, b) => (a.assetName || '').localeCompare(b.assetName || ''))
                )
              ]}
              getOptionLabel={(option) => {
                if (!option) return '';
                if (option.assetID === 0) return "Select Asset"; 
                return `${option?.assetName || ''}`.trim();
              }}
              value={
                asset?.assetID
                  ? assetsList.find(item => Number(item.assetID) === Number(asset.assetID))
                  : { assetID: 0, assetName: "Select Asset"}
              }            
              onChange={(event, newValue) => {              
                  handleAssetChange("assetID", newValue.assetID || 0);              
              }}                                              
              isOptionEqualToValue={(option, value) =>
                option?.assetID === value?.assetID
              }
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option => 
                  option.assetID === 0 || // Always include the "Select Asset" option
                  (option.assetName || '').toLowerCase().includes(searchTerm)
                );
              }}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Asset Name"
                  placeholder="Select Asset Name"
                  required
                  error={!!assetErrors?.assetID}
                  helperText={assetErrors?.assetID}
                />
              )}
            />
          </Grid> 
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth
            label="Quantity"
            placeholder="5"
            required
            value={isActuals ? asset?.actualQuantity : asset?.budgetedQuantity}
            onChange={(e) =>
              handleAssetChange(isActuals ? "actualQuantity" : "budgetedQuantity", e.target.value)
            }
            error={!!assetErrors?.budgetedQuantity}
            helperText={assetErrors?.budgetedQuantity}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth
            label={isActuals ? "Actual Usage Duration" : "Estimate Usage Duration"}
            placeholder="10 Hours"
            InputProps={{
              endAdornment: <InputAdornment position="end">{asset.DurationUnit}</InputAdornment>,
            }}            
            required
            value={isActuals ? asset?.actualDuration : asset?.budgetedDuration}
            onChange={(e) =>
              handleAssetChange(isActuals ? "actualDuration" : "budgetedDuration", e.target.value)
            }
            error={!!assetErrors?.budgetedDuration}
            helperText={assetErrors?.budgetedDuration}
          />
        </Grid>  
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth
            required
            label={isActuals ? "Actual Cost Per Asset" : "Cost Per Asset"}
            placeholder="220$"            
            InputProps={{
              endAdornment: <InputAdornment position="end">$</InputAdornment>,
            }}            
            value={formatNumberWithCommas(isActuals ? asset?.actualCostPerUnit : asset?.budgetedCostPerUnit)}
            onChange={(e) =>
              handleAssetChange(isActuals ? "actualCostPerUnit" : "budgetedCostPerUnit", removeCommas(e.target.value))
            }
            error={!!assetErrors?.budgetedCostPerUnit}
            helperText={assetErrors?.budgetedCostPerUnit}           
          />
        </Grid>  
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth            
            label={isActuals ? "Actual Price Per Asset" : "Price Per Asset"}
            placeholder="220$"
            InputProps={{
              endAdornment: <InputAdornment position="end">$</InputAdornment>,
            }}
            required = {!!asset.billToCustomer}
            value={formatNumberWithCommas(isActuals ? asset?.actualPricePerUnit : asset?.budgetedPricePerUnit)}
            onChange={(e) =>
              handleAssetChange(isActuals ? "actualPricePerUnit" : "budgetedPricePerUnit", removeCommas(e.target.value))
            }
            error={!!assetErrors?.budgetedPricePerUnit}
            helperText={assetErrors?.budgetedPricePerUnit}
          />
        </Grid>           
       <Grid item xs={12} md={6}>                  
        <CustomAutocomplete
          fullWidth
          clearOnBlur={false}  
          clearOnEscape
          options={[
            { employeeID: 0, firstName: "Select", lastName: "Provided By" }, 
            ...((employeeList?.filter(x => x.roleID === 7) || [])
                .sort((a, b) => 
                  `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`)
                )
            )
          ]}
          getOptionLabel={(option) => {
            if (!option) return '';
            if (option.employeeID === 0) return "Select Provided by";
            return `${option.firstName || ''} ${option.lastName || ''}`.trim();
          }}
          value={
            employeeList?.find(item => item.employeeID === asset?.providedBy) ||
            { employeeID: 0, firstName: "Select", lastName: "Provided by" }
          }
          onChange={(event, newValue) => {
            handleAssetChange("providedBy", newValue?.employeeID || 0);
          }}
          isOptionEqualToValue={(option, value) =>
            option?.employeeID === value?.employeeID
          }
          filterOptions={(options, { inputValue }) => {
            const searchTerm = inputValue.toLowerCase();
            return options.filter(option => 
              option.employeeID === 0 || // Always include the "Select Provided By" option
              `${option.firstName || ''} ${option.lastName || ''}`.toLowerCase().includes(searchTerm)
            );
          }}
          renderInput={(params) => (
            <CustomTextField
              {...params}
              label="Provided By"
              placeholder="Select Provided by"
            />
          )}
        />
      </Grid>
        <Grid item xs={12} md={6} sx={{display: 'flex',alignItems: 'center'}}>
          <Box>
            <FormLabel sx={{ mr: 2 }}>Bill to Customer</FormLabel>
            <label class="switch">
              <input type="checkbox" checked={asset.billToCustomer ?? false}
                onChange={(e) => {
                  handleAssetChange("billToCustomer", e.target.checked);
                }} />
              <span class="slider round"></span>
            </label>
            <FormLabel sx={{ ml: 2 }}>{asset.billToCustomer ? "Yes" : "No"}</FormLabel>
          </Box>
        </Grid>   
      </Grid>

    </>
  ), [asset, assetErrors, employeeList]);

  const Observation = useMemo(() => (
    <>
      <Grid container spacing={2}>        
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth
            label="Observation Name"
            placeholder="Observation Name"
            required
            value={observation?.observationName}
            onChange={(e) => handleObservationChange("observationName", e.target.value)}
            error={!!observationErrors?.observationName}
            helperText={observationErrors?.observationName}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth
            label="Description"
            placeholder="Description"
            required
            value={observation?.observationDescription}
            onChange={(e) => handleObservationChange("observationDescription", e.target.value)}
            error={!!observationErrors?.observationDescription}
            helperText={observationErrors?.observationDescription}
          />
        </Grid>
      </Grid>
    </>
  ), [observation, observationErrors]);

  const Material = useMemo(() => (
    <>
      <Grid container spacing={2}>
       <Grid item xs={12} md={6}>
              <CustomAutocomplete
                fullWidth
                required
                options={(materialTypesList || []).sort((a, b) => 
                  (a.typeName || '').localeCompare(b.typeName || '')
                )}
                getOptionLabel={(option) => option?.typeName || ''}
                value={
                  material?.materialTypeID
                    ? materialTypesList.find(item => Number(item.typeCatalogID) === Number(material.materialTypeID))
                    : { typeCatalogID: 0, typeName: "Select Material Type"}
                }
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleMaterialChange("materialTypeID", newValue.typeCatalogID);                
                  } else {                
                    handleMaterialChange("materialTypeID", 0); 
                    handleMaterialChange("materialID", 0);
                  }
                }}
                isOptionEqualToValue={(option, value) =>
                  option?.typeCatalogID === value?.typeCatalogID
                }
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option => 
                    (option.typeName || '').toLowerCase().includes(searchTerm)
                  );
                }}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label="Material Type"
                    placeholder="Select Material Type"
                    required
                    error={!!materialErrors?.materialTypeID}
                    helperText={materialErrors?.materialTypeID}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>          
              <CustomAutocomplete
                fullWidth
                required
                disabled={!material?.materialTypeID}
                options={[
                  {materialID: 0, itemName: "Select Material"}, 
                  ...((materialsList?.filter(x => x.materialTypeID === material?.materialTypeID) || [])
                      .sort((a, b) => (a.itemName || '').localeCompare(b.itemName || ''))
                  )
                ]}
                getOptionLabel={(option) =>{
                    if (!option) return '';
                    if (option.materialID === 0) return "Select Material";
                    return `${option.itemName || ''}`.trim();
                  }}
                value={
                  material?.materialID
                    ? materialsList.find(item => Number(item.materialID) === Number(material.materialID))
                    : { materialID: 0, itemName: "Select Material"}
                }
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleMaterialChange("materialID", newValue? newValue.materialID : 0);
                  }
                }}
                isOptionEqualToValue={(option, value) =>
                  option?.materialID === value?.materialID
                }
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option => 
                    option.materialID === 0 || 
                    (option.itemName || '').toLowerCase().includes(searchTerm)
                  );
                }}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label="Material Name"
                    placeholder="Select Material Name"
                    required
                    error={!!materialErrors?.materialID}
                    helperText={materialErrors?.materialID}
                  />
                )}
              />
            </Grid>
                    <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label="Quantity"
                required
                placeholder="5"
                InputProps={{
                  endAdornment: <InputAdornment position="end" sx={{
                    color: (isActuals ? material?.actualQuantity : material?.budgetedQuantity) > material?.availableQuantity ? "#EA5455" : "inherit"
                  }}>
                    {material?.availableQuantity ? material?.availableQuantity + " in stock" : ""}
                  </InputAdornment>,
                }}

                value={isActuals ? material?.actualQuantity : material?.budgetedQuantity}
                onChange={(e) =>
                  handleMaterialChange(isActuals ? "actualQuantity" : "budgetedQuantity", e.target.value)
                }
                error={!!materialErrors?.budgetedQuantity}
                helperText={materialErrors?.budgetedQuantity}            
              />
            </Grid>
           <Grid item xs={12} md={6}>
            <CustomAutocomplete
              fullWidth
              required
              options={unitOptions.sort((a, b) => a.label.localeCompare(b.label))}
              value={
                unitOptions.find(option => option.value === material?.unitID) || null
              }
              onChange={handleMaterialUnitChange}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option => 
                  option.label.toLowerCase().includes(searchTerm)
                );
              }}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Unit of Measure"
                  required
                  placeholder="Select Unit of Measure"
                  error={!!materialErrors?.unitOfMeasure}
                  helperText={materialErrors?.unitOfMeasure}
                  InputLabelProps={{
                    sx: {
                      mb: 2, 
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: option.isAddNew ? 'bold' : 'normal',
                    }}
                  >
                    {option.label}
                  </Typography>
                </li>
              )}
              PaperProps={{
                sx: {
                  maxHeight: 200
                }
              }}
            />
          </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth          
            label={isActuals ? "Actual Cost Per Unit" : "Cost Per Unit"}
            placeholder="220$"            
            InputProps={{
              endAdornment: <InputAdornment position="end">$</InputAdornment>,
            }}
            required
            value={formatNumberWithCommas(isActuals ? material?.actualCostPerUnit : material?.budgetedCostPerUnit)}
            onChange={(e) =>
              handleMaterialChange(isActuals ? "actualCostPerUnit" : "budgetedCostPerUnit", removeCommas(e.target.value))
            }
            error={!!materialErrors?.budgetedCostPerUnit}
            helperText={materialErrors?.budgetedCostPerUnit}           
          />
        </Grid>   
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth
            required
            label={isActuals ? "Actual Price Per Unit" : "Price Per Unit"}
            placeholder="220$"
            InputProps={{
              endAdornment: <InputAdornment position="end">$</InputAdornment>,
            }}            
            value={formatNumberWithCommas(isActuals ? material?.actualPricePerUnit : material?.budgetedPricePerUnit)}
            onChange={(e) =>
              handleMaterialChange(isActuals ? "actualPricePerUnit" : "budgetedPricePerUnit", removeCommas(e.target.value))
            }
            error={!!materialErrors?.budgetedPricePerUnit}
            helperText={materialErrors?.budgetedPricePerUnit} 
          />
        </Grid>   
      <Grid item xs={12} md={6}>          
        <CustomAutocomplete
          fullWidth
          clearOnBlur={false}  // Important to prevent clearing when clicking away
          clearOnEscape
          options={[
            { employeeID: 0, firstName: "Select", lastName: "Provided By" }, // Default option
            ...((employeeList?.filter(x => x.roleID === 7) || [])
                .sort((a, b) => 
                  `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`)
                )
            )
          ]}
          getOptionLabel={(option) => {
            if (!option) return '';
            if (option.employeeID === 0) return "Select Provided by";
            return `${option.firstName || ''} ${option.lastName || ''}`.trim();
          }}
          value={
            employeeList?.find(item => item.employeeID === material?.providedBy) ||
            { employeeID: 0, firstName: "Select", lastName: "Provided by" }
          }
          onChange={(event, newValue) => {
            handleMaterialChange("providedBy", newValue?.employeeID || 0);
          }}
          isOptionEqualToValue={(option, value) =>
            option?.employeeID === value?.employeeID
          }
          filterOptions={(options, { inputValue }) => {
            const searchTerm = inputValue.toLowerCase();
            return options.filter(option => 
              option.employeeID === 0 || 
              `${option.firstName || ''} ${option.lastName || ''}`.toLowerCase().includes(searchTerm)
            );
          }}
          renderInput={(params) => (
            <CustomTextField
              {...params}
              label="Provided By"
              placeholder="Select Provided by"
            />
          )}
        />
      </Grid>  
            </Grid>
          </>
  ), [material, materialErrors, units, unitOptions]);

  const Labor = useMemo(() => (
    <>    
      <Grid container spacing={2}>
       <Grid item xs={12} md={6}>
          <CustomAutocomplete
            fullWidth
            options={(skillTypesList || []).sort((a, b) => 
              (a.skillName || '').localeCompare(b.skillName || '')
            )}
            getOptionLabel={(option) => option?.skillName || ''}
            value={
              skillTypesList?.find(item => item.skillID === labor?.skillID)
            }
            onChange={(event, newValue) => {
              handleLaborChange("skillID", newValue?.skillID || 0);
            }}
            isOptionEqualToValue={(option, value) =>
              option?.skillID === value?.skillID
            }
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              return options.filter(option => 
                (option.skillName || '').toLowerCase().includes(searchTerm)
              );
            }}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label="Skill Type"
                placeholder="Select Skill Type"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomAutocomplete
            fullWidth   
            clearOnBlur={false}  
            clearOnEscape      
            disableClearable={false}         
            // disabled={!labor?.skillID}                      
           options={
            ((!labor?.skillID || labor?.skillID === 0) 
              ? (employeeList?.filter(x => x.empType !== 'Sub Contractors') || [])
              : (employeeList?.filter(x => x.skillID === labor?.skillID && x.empType !== 'Sub Contractors') || [])
            )
            .sort((a, b) => 
              `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`)
            )
          }
            getOptionLabel={(option) => {
              if (!option) return '';
              if (option.employeeID === 0) return "";
              return `${option.firstName || ''} ${option.lastName || ''}`.trim();
            }}
            value={
              employeeList?.find(item => item.employeeID === labor?.employeeID && item.empType !== 'Sub Contractors') ||
              { employeeID: 0, firstName: "", lastName: "" }
            }                         
            onChange={(event, newValue) => {
              if (newValue) {
                handleLaborChange("employeeID", newValue.employeeID || 0);
              } else {
                // Clear selected employee and reset to default
                handleLaborChange("employeeID", 0);
              }
            }}
            isOptionEqualToValue={(option, value) =>
              option?.employeeID === value?.employeeID
            }
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              return options.filter(option => 
                `${option.firstName || ''} ${option.lastName || ''}`.toLowerCase().includes(searchTerm)
              );
            }}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label="Employee Name"
                placeholder="Select Employee Name"                
                //error={!!laborErrors?.employeeID}
                //helperText={laborErrors?.employeeID}
              />
            )}
          />
        </Grid>      
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth            
            label={isActuals ? "Actual Man Hours" : "Man Hours"}
            placeholder="40"
            InputProps={{
              endAdornment: <InputAdornment position="end">Hrs</InputAdornment>,
            }}
            required
            value={isActuals ? labor?.actualHours : labor?.budgetedHours}
            onChange={(e) =>
              handleLaborChange(isActuals ? "actualHours" : "budgetedHours", e.target.value)
            }
            error={!!(isActuals
              ? laborErrors.actualHours
              : laborErrors.budgetedHours)}
            helperText={
              isActuals
                ? laborErrors.actualHours
                : laborErrors.budgetedHours
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth 
            required                       
            label={isActuals ? "Actual Cost Per Hour" : "Cost Per Hour"}
            InputProps={{
              endAdornment: <InputAdornment position="end">$</InputAdornment>,
            }}
            placeholder="40"            
            value={formatNumberWithCommas(labor?.budgetedCostPerHour || labor.actualCostPerHour || 0)}
            onChange={(e) =>
              handleLaborChange("budgetedCostPerHour", removeCommas(e.target.value))
            }
            error={!!(isActuals
              ? laborErrors.actualCostPerHour
              : laborErrors.budgetedCostPerHour)}
            helperText={
              isActuals
                ? laborErrors.actualCostPerHour
                : laborErrors.budgetedCostPerHour
            }
          />
        </Grid>
     <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth                        
            label="Markup"
            placeholder="12%"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            required
            value={labor?.markup}
            onChange={(e) => handleLaborChange("markup", e.target.value)}
            onBlur={(e) => {
              const value = e.target.value;
              if (value && value.trim() !== '') {
                const numericValue = parseFloat(value);
                if (!isNaN(numericValue)) {
                  const roundedValue = Math.round(numericValue);
                  handleLaborChange("markup", roundedValue.toString());
                }
              }
            }}
            error={!!laborErrors?.markup}
            helperText={laborErrors?.markup}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomAutocomplete
            fullWidth
            clearOnBlur={false}  
            clearOnEscape
            options={[
              { employeeID: 0, firstName: "Select", lastName: "Provided By" }, 
              ...((employeeList?.filter(x => x.roleID === 7) || [])
                  .sort((a, b) => 
                    `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`)
                  )
              )
            ]}
            getOptionLabel={(option) => {
              if (!option) return '';
              if (option.employeeID === 0) return "Select Provided by";
              return `${option.firstName || ''} ${option.lastName || ''}`.trim();
            }}
            value={
              employeeList?.find(item => item.employeeID === labor?.providedBy) ||
              { employeeID: 0, firstName: "Select", lastName: "Provided by" }
            }
            onChange={(event, newValue) => {
              handleLaborChange("providedBy", newValue?.employeeID || 0);
            }}
            isOptionEqualToValue={(option, value) =>
              option?.employeeID === value?.employeeID
            }
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              return options.filter(option => 
                option.employeeID === 0 || // Always include the "Select Provided By" option
                `${option.firstName || ''} ${option.lastName || ''}`.toLowerCase().includes(searchTerm)
              );
            }}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label="Provided By"
                placeholder="Select Provided by"
              />
            )}
          />
        </Grid>
      </Grid>

    </>
  ), [labor, laborErrors]);

  const Subcontractor = useMemo(() => (
    <>    
      <Grid container spacing={2}>
       <Grid item xs={12} md={6}>
          <CustomAutocomplete
            fullWidth
            required
            options={(skillTypesList || []).sort((a, b) => 
              (a.skillName || '').localeCompare(b.skillName || '')
            )}
            getOptionLabel={(option) => option?.skillName || ''}
            value={
              skillTypesList?.find(item => item.skillID === subcontractor?.skillID)
            }
            onChange={(event, newValue) => {
              handleSubcontractorChange("skillID", newValue?.skillID || 0);
            }}
            isOptionEqualToValue={(option, value) =>
              option?.skillID === value?.skillID
            }
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              return options.filter(option => 
                (option.skillName || '').toLowerCase().includes(searchTerm)
              );
            }}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label="Skill Type"
                placeholder="Select Skill Type"
                required
                error={!!subcontractorErrors?.skillID}
                helperText={subcontractorErrors?.skillID}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomAutocomplete
            fullWidth   
            required
            clearOnBlur={false}  
            clearOnEscape      
            disableClearable={false}         
            disabled={!subcontractor?.skillID}                      
            options={(subcontractorList?.filter(x => x.skillID === subcontractor?.skillID) || [])
              .sort((a, b) => 
                `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`)
              )
            }
            getOptionLabel={(option) => {
              if (!option) return '';
              if (option.employeeID === 0) return "";
              return `${option.firstName || ''} ${option.lastName || ''}`.trim();
            }}
            value={
              subcontractorList?.find(item => item.employeeID === subcontractor?.employeeID) ||
              { employeeID: 0, firstName: "", lastName: "" }
            }                         
            onChange={(event, newValue) => {
              if (newValue) {
                handleSubcontractorChange("employeeID", newValue.employeeID || 0);
              } else {
                // Clear selected employee and reset to default
                handleSubcontractorChange("employeeID", 0);
              }
            }}
            isOptionEqualToValue={(option, value) =>
              option?.employeeID === value?.employeeID
            }
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              return options.filter(option => 
                `${option.firstName || ''} ${option.lastName || ''}`.toLowerCase().includes(searchTerm)
              );
            }}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label="Subcontractor Name"
                required
                placeholder="Select Subcontractor Name"                
                error={!!subcontractorErrors?.employeeID}
                helperText={subcontractorErrors?.employeeID}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {/* <CustomAutocomplete
            fullWidth
            required
            options={['Hourly','Salary','Per Day','Per Job','Per Visit']}
            getOptionLabel={(option) => option || ''}
            value={subcontractor?.unitType}
            onChange={(event, newValue) => {
              handleSubcontractorChange("unitType", newValue);
            }}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label="Unit Type"
                placeholder="Select Unit Type"
                required
                error={!!subcontractorErrors?.unitType}
                helperText={subcontractorErrors?.unitType}
              />
            )}
          /> */}
          <CustomAutocomplete
            fullWidth
            required
            options={unitOptions.sort((a, b) => a.label.localeCompare(b.label))}
            value={
              unitOptions.find(option => option.value === subcontractor?.unitID) || null
            }
            onChange={handleSubcontractorUnitChange}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              return options.filter(option => 
                option.label.toLowerCase().includes(searchTerm)
              );
            }}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label="Unit of Measure"
                required
                placeholder="Select Unit of Measure"
                error={!!subcontractorErrors?.unitID}
                helperText={subcontractorErrors?.unitID}
                InputLabelProps={{
                  sx: {
                    mb: 2, 
                  }
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: option.isAddNew ? 'bold' : 'normal',
                  }}
                >
                  {option.label}
                </Typography>
              </li>
            )}
            PaperProps={{
              sx: {
                maxHeight: 200
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth            
            label={isActuals ? "Actual Unit Quantity" : "Unit Quantity"}
            placeholder="40"
            required
            // disabled={subcontractor?.unitType === 'Per Job' || subcontractor?.unitType === 'Salary'}
            value={isActuals ? subcontractor?.actualUnitQuantity : subcontractor?.budgetedUnitQuantity}
            onChange={(e) =>
              handleSubcontractorChange(isActuals ? "actualUnitQuantity" : "budgetedUnitQuantity", e.target.value)
            }
            error={!!(isActuals
              ? subcontractorErrors.actualUnitQuantity
              : subcontractorErrors.budgetedUnitQuantity)}
            helperText={
              isActuals
                ? subcontractorErrors.actualUnitQuantity
                : subcontractorErrors.budgetedUnitQuantity
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth 
            required                       
            label={isActuals ? "Actual Cost Per Unit" : "Cost Per Unit"}
            InputProps={{
              endAdornment: <InputAdornment position="end">$</InputAdornment>,
            }}
            placeholder="40"            
            value={formatNumberWithCommas(isActuals ? subcontractor.actualCostPerUnit : subcontractor?.budgetedCostPerUnit )}
            onChange={(e) =>
              handleSubcontractorChange(isActuals ?  "actualCostPerUnit" : "budgetedCostPerUnit", removeCommas(e.target.value))
            }
            error={!!(isActuals
              ? subcontractorErrors.actualCostPerUnit
              : subcontractorErrors.budgetedCostPerUnit)}
            helperText={
              isActuals
                ? subcontractorErrors.actualCostPerHour
                : subcontractorErrors.budgetedCostPerHour
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth 
            required                       
            label={isActuals ? "Actual Price Per Unit" : "Price Per Unit"}
            InputProps={{
              endAdornment: <InputAdornment position="end">$</InputAdornment>,
            }}
            placeholder="40"            
            value={formatNumberWithCommas(isActuals ? subcontractor.actualPricePerUnit : subcontractor?.budgetedPricePerUnit )}
            onChange={(e) =>
              handleSubcontractorChange(isActuals ?  "actualPricePerUnit" : "budgetedPricePerUnit", removeCommas(e.target.value))
            }
            error={!!(isActuals
              ? subcontractorErrors.actualPricePerUnit
              : subcontractorErrors.budgetedPricePerUnit)}
            helperText={
              isActuals
                ? subcontractorErrors.actualPricePerUnit
                : subcontractorErrors.budgetedPricePerUnit
            }
          />
        </Grid>
       <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth                        
            label="Markup"
            placeholder="12%"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            required
            value={isActuals ? subcontractor.actualMarkup : subcontractor.budgetedMarkup}
            onChange={(e) => handleSubcontractorChange(
              isActuals ? "actualMarkup" : "budgetedMarkup", 
              e.target.value
            )}
            onBlur={(e) => {
              const value = e.target.value;
              if (value && value.trim() !== '') {
                const numericValue = parseFloat(value);
                if (!isNaN(numericValue)) {
                  const roundedValue = Math.round(numericValue);
                  handleSubcontractorChange(
                    isActuals ? "actualMarkup" : "budgetedMarkup", 
                    roundedValue.toString()
                  );
                }
              }
            }}
            error={!!(isActuals
              ? subcontractorErrors.actualMarkup
              : subcontractorErrors.budgetedMarkup)}
            helperText={
              isActuals
                ? subcontractorErrors.actualMarkup
                : subcontractorErrors.budgetedMarkup
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomAutocomplete
            fullWidth
            clearOnBlur={false}  
            clearOnEscape
            options={[
              { employeeID: 0, firstName: "Select", lastName: "Provided By" }, 
              ...((employeeList?.filter(x => x.roleID === 7) || [])
                  .sort((a, b) => 
                    `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`)
                  )
              )
            ]}
            getOptionLabel={(option) => {
              if (!option) return '';
              if (option.employeeID === 0) return "Select Provided by";
              return `${option.firstName || ''} ${option.lastName || ''}`.trim();
            }}
            value={
              employeeList?.find(item => item.employeeID === labor?.providedBy) ||
              { employeeID: 0, firstName: "Select", lastName: "Provided by" }
            }
            onChange={(event, newValue) => {
              handleSubcontractorChange("providedBy", newValue?.employeeID || 0);
            }}
            isOptionEqualToValue={(option, value) =>
              option?.employeeID === value?.employeeID
            }
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              return options.filter(option => 
                option.employeeID === 0 || // Always include the "Select Provided By" option
                `${option.firstName || ''} ${option.lastName || ''}`.toLowerCase().includes(searchTerm)
              );
            }}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label="Provided By"
                placeholder="Select Provided by"
              />
            )}
          />
        </Grid>
      </Grid>
    </>
  ), [subcontractor, subcontractorErrors]);

  const MiscCost = useMemo(() => (
    <>
      <Grid container spacing={2}>        
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth
            label="Cost Name"
            placeholder="Cost Name"
            required
            value={miscCost?.costName}
            onChange={(e) => handleMiscCostChange("costName", e.target.value)}
            error={!!miscCostErrors?.costName}
            helperText={miscCostErrors?.costName}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth
            label="Description"
            placeholder="Description"
            
            value={miscCost?.costDescription}
            onChange={(e) => handleMiscCostChange("costDescription", e.target.value)}
            //error={!!miscCostErrors?.costDescription}
           // helperText={miscCostErrors?.costDescription}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth
            label="Cost Amount"
            placeholder="Cost Amount"
            InputProps={{
              endAdornment: <InputAdornment position="end">$</InputAdornment>,
            }}
            required
            value={formatNumberWithCommas(isActuals ? miscCost?.actualCostAmount : miscCost?.budgetedCostAmount)}
            onChange={(e) =>
              handleMiscCostChange(isActuals ? "actualCostAmount" : "budgetedCostAmount", removeCommas(e.target.value))
            }
            error={!!miscCostErrors?.budgetedCostAmount}
            helperText={miscCostErrors?.budgetedCostAmount}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth
            label="Customer Price"
            placeholder="Customer Price"
            InputProps={{
              endAdornment: <InputAdornment position="end">$</InputAdornment>,
            }}
            required={miscCost.billToCustomer}
            value={formatNumberWithCommas(isActuals ? miscCost?.actualCustomerPrice : miscCost?.budgetedCustomerPrice)}
            onChange={(e) =>
              handleMiscCostChange(isActuals ? "actualCustomerPrice" : "budgetedCustomerPrice", removeCommas(e.target.value))
            }
            error={!!miscCostErrors?.budgetedCustomerPrice}
            helperText={miscCostErrors?.budgetedCustomerPrice}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box>
            <FormLabel sx={{ mr: 2 }}>Bill to Customer</FormLabel>
            <label class="switch">
              <input type="checkbox" checked={miscCost.billToCustomer ?? false}
                onChange={(e) => {
                  handleMiscCostChange("billToCustomer", e.target.checked);
                }} />
              <span class="slider round"></span>
            </label>
            <FormLabel sx={{ ml: 2 }}>{miscCost.billToCustomer ? "Yes" : "No"}</FormLabel>
          </Box>
        </Grid>
      </Grid>
    </>
  ), [miscCost, miscCostErrors]);

  return (
    <>
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #D9D9D9",
          paddingBottom: "20px !important",
        }}
      >
        <Typography variant="h4">{LineItemContent.Title}</Typography>
        <IconButton onClick={handleClose}>
          <Icon icon="tabler:x" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {LineItemContent.Type === "Asset" && (
          <Box>
            {Asset}
          </Box>
        )
        }
        {LineItemContent.Type === "Observation" && (
          <Box>
            {Observation}
          </Box>
        )
        }
        {LineItemContent.Type === "Material" && (
          <Box>
            {Material}
          </Box>
        )
        }
        {LineItemContent.Type === "Labor" && (
          <Box>
            {Labor}
          </Box>
        )
        }
        {LineItemContent.Type === "Subcontractor" && (
          <Box>
            {Subcontractor}
          </Box>
        )
        }
        {LineItemContent.Type === "MiscCost" && (
          <Box>
            {MiscCost}
          </Box>
        )
        }
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "20px 24px !important",
          boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
          zIndex: 1,
        }}
      >
        <Button variant="contained" color="info" onClick={handleClose}>
          Cancel
        </Button>
       <Button
        variant="contained"
        color="main"
        disabled={
          LineItemContent.Type === "Labor" &&
          !(labor?.skillID || labor?.employeeID)
        }
        onClick={() => handleSave(LineItemContent.Type)}
      >
        {LineItemContent.Title.includes("Edit")
          ? "Save Changes"
          : LineItemContent.Title}
      </Button>

      </DialogActions>
    </Dialog>
      <UnitDialog
        open={unitDialogOpen}
        handleClose={() => setUnitDialogOpen(false)}
        UnitProps={{
          existingUnit: null,
          fetchUnits: fetchUnitsData,
          unitContent: {
            title: "Add Unit of Measurement",
            entity: 'create'
          },
          onUnitCreated: handleUnitCreatedCallback
        }}
      />
    </>
  );
};

export default AddLineItem;
